import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import PageHero from "../components/ui-custom/PageHero";
import GlassCard from "../components/ui-custom/GlassCard";
import Reveal from "../components/ui-custom/Reveal";
import NewsFeatureSection from "../components/home/NewsFeatureSection";
import { formatDateTime } from "../lib/formatDate";
import { Newspaper, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Tab label <-> ?tab= value, matching the header's Media dropdown links.
const tabs = [
  { label: "All", param: null },
  { label: "News", param: "news" },
  { label: "Corporate News", param: "corporate" },
];

const formatNewsDate = (item) => {
  const d = new Date(item.published_at || item.pub_date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const Media = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabs.find((t) => t.param === searchParams.get("tab")) || tabs[0];

  const [items, setItems] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/media/news`)
      .then((res) => {
        setItems(res.data.items || []);
        setFetchedAt(res.data.fetched_at);
      })
      .catch((err) => {
        setItems([]);
        setError(err.response?.data?.detail || `${err.name}: ${err.message}`);
      });
  }, []);

  const selectTab = (t) => setSearchParams(t.param ? { tab: t.param } : {});
  const filtered = (items || []).filter((n) => activeTab.param === null || n.category === activeTab.label);

  return (
    <div>
      <PageHero eyebrow="Media Center" title="Newsroom" subtitle="Latest news and corporate announcements from Quality Power." />

      <NewsFeatureSection className="pt-4 pb-16" />

      <section className="px-6 pb-8">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-3 mb-4">
          {tabs.map((t) => (
            <button
              key={t.label}
              onClick={() => selectTab(t)}
              className={`px-5 py-2 rounded-full text-sm font-medium tracking-wide transition-colors border ${
                activeTab === t ? "bg-qcyan/10 border-qcyan/50 text-qcyan" : "border-border/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {fetchedAt && (
          <p className="text-center text-xs text-muted-foreground mb-10">Updated {formatDateTime(fetchedAt)}</p>
        )}
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto grid gap-5">
          {items === null && <p className="text-sm text-muted-foreground text-center">Loading latest headlines…</p>}
          {error && <p className="text-sm text-destructive text-center">Could not load news: {error}</p>}
          {items !== null && !error && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center">No headlines in this category yet.</p>
          )}
          {filtered.map((news, i) => (
            <Reveal key={news.link} delay={Math.min(i, 10) * 0.05}>
              <a href={news.link} target="_blank" rel="noopener noreferrer" className="block group">
                <GlassCard className="p-6 flex gap-4 items-start transition-colors group-hover:border-qcyan/40">
                  <span className="w-11 h-11 rounded-lg bg-qcyan/10 border border-qcyan/25 flex items-center justify-center shrink-0">
                    <Newspaper size={18} className="text-qcyan" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                      <span className="text-xs text-qamber uppercase tracking-wide font-semibold">{news.category}</span>
                      <span className="text-xs text-muted-foreground">
                        {news.source || "News"}
                        {formatNewsDate(news) ? ` · ${formatNewsDate(news)}` : ""}
                      </span>
                    </div>
                    <h3 className="font-display font-semibold text-foreground text-base group-hover:text-qcyan transition-colors">
                      {news.title}
                    </h3>
                  </div>
                  <ExternalLink size={16} className="text-muted-foreground group-hover:text-qcyan shrink-0 mt-1" />
                </GlassCard>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Media;
